/**
 * @file 学びメモの保存の前後を比べ、書き足した行と足した画像の数を出す純関数である。
 *
 * @what: 直前に保存した本文と今回の本文を行ごとに比べ、今回にだけある行 (同じ行が増えたぶんも含む)
 *   を「書き足した行」として返す。画像の参照の行は文に数えず、画像の一覧の差で数える。
 * @why: 「さいきん書いたメモ」に出すのは、その日に書いた文である。本文の全量を日ごとに残すのではなく、
 *   保存のたびに足したぶんだけを送る (model の説明は api/noteEntries.ts)。行の多重集合の差にするのは、
 *   行を入れ替えたり前の行を直したりしただけでは「書き足した」と数えないためである。
 * @failure: 副作用を持たない。文も画像も増えていなければ null を返す (送るものが無い)。
 */

import { stripNoteImageRefs } from "./noteImages";

export interface NoteEntrySnapshot {
  body: string;
  imageIds: readonly string[];
}

export interface NoteEntryDiff {
  /** 書き足した行 (改行区切り) である。無ければ空にする。 */
  text: string;
  /** 足した画像の数である。 */
  images: number;
}

/** 行として数えない行 (空行と画像の参照だけの行)。 */
function isNoise(line: string): boolean {
  return stripNoteImageRefs(line).trim() === "";
}

/** 直前の本文に無い行を、今回の本文の並びのまま返す。 */
export function addedLines(prevBody: string, nextBody: string): string[] {
  const counts = new Map<string, number>();
  for (const raw of prevBody.split("\n")) {
    const line = raw.trim();
    if (isNoise(line)) continue;
    counts.set(line, (counts.get(line) ?? 0) + 1);
  }
  const out: string[] = [];
  for (const raw of nextBody.split("\n")) {
    const line = raw.trim();
    if (isNoise(line)) continue;
    const n = counts.get(line) ?? 0;
    if (n > 0) {
      counts.set(line, n - 1);
      continue;
    }
    out.push(line);
  }
  return out;
}

export function noteEntryDiff(prev: NoteEntrySnapshot, next: NoteEntrySnapshot): NoteEntryDiff | null {
  const text = addedLines(prev.body, next.body).join("\n");
  const known = new Set(prev.imageIds);
  const images = next.imageIds.filter((id) => !known.has(id)).length;
  if (text === "" && images === 0) return null;
  return { text, images };
}
